import { 
  WalletManager,
  WalletId,
  NetworkId,
  LogLevel
} from '@txnlab/use-wallet';
import { Algodv2 } from "algosdk";
import { getNodeEndpoint } from '../../config';
import { notify } from '../../utils/toast';

// Export for use in other files
export { WalletId, WalletManager };

// Define wallet types for backward compatibility
export type WalletType = 
  | 'pera' 
  | 'defly' 
  | 'daffi' 
  | 'lute'
  | 'exodus'
  | 'kibisis'
  | string;

// Map old wallet types to new wallet IDs
export const walletTypeToWalletId = (walletType: WalletType): WalletId => {
  switch (walletType) {
    case 'pera':
      return WalletId.PERA;
    case 'defly':
      return WalletId.DEFLY;
    case 'daffi':
      // Note: Daffi might not be directly supported in the latest version
      // Use WalletConnect instead if needed
      return WalletId.WALLETCONNECT;
    case 'lute':
      return WalletId.LUTE;
    case 'exodus':
      return WalletId.EXODUS;
    case 'kibisis':
      return WalletId.KIBISIS;
    default:
      return walletType as WalletId;
  }
};

// Create and export wallet manager instance
export const walletManager = new WalletManager({
  // Configure wallets - Pera is first as the default
  wallets: [
    WalletId.PERA, // Default wallet
    WalletId.DEFLY,
    WalletId.LUTE,
    WalletId.EXODUS,
    WalletId.KIBISIS
  ],
  // Use mainnet only as specified
  defaultNetwork: NetworkId.MAINNET,
  // Additional options
  options: {
    debug: false,
    logLevel: LogLevel.WARN
  }
});

// Initialize wallet manager
walletManager.resumeSessions().catch(error => {
  console.error('Failed to resume wallet sessions:', error);
});

// For backward compatibility
export const createWalletInstance = (walletType: WalletType) => {
  console.warn('createWalletInstance is deprecated. Use walletManager instead.');
  const walletId = walletTypeToWalletId(walletType);
  return walletManager.getWallet(walletId);
};

// For backward compatibility
export const connectWallet = async (wallet: any) => {
  console.warn('connectWallet is deprecated. Use walletManager instead.');
  try {
    if (wallet) {
      await wallet.connect();
      return walletManager.activeAccount?.address ? [walletManager.activeAccount.address] : [];
    }
    return [];
  } catch (error) {
    console.error('Error connecting wallet:', error);
    return [];
  }
};

// Setup Algod client (keep this function as is)
export const setupAlgodClient = () => {
  return new Algodv2('', getNodeEndpoint(), '');
};

// Attempt to reconnect to previously connected wallets
export const attemptWalletReconnect = async () => {
  try {
    // In the new API, reconnection is handled automatically by the wallet manager
    // when it's initialized. We'll just return true here for backward compatibility.
    console.log('Wallet reconnection is now handled automatically by the wallet manager');
    return true;
  } catch (error) {
    console.error('Failed to reconnect wallet:', error);
    return false;
  }
};
